import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PenLine, MessageSquareText, Loader2, Check, ArrowRight, Brain, GitBranch, X, Layers, Sparkles, RefreshCw } from "lucide-react";
import { essayState, essayTurn, essaySaveThinking, essayReaderOrder, essayReturnToThinking } from "@/lib/api";
import { otNoPaste } from "@/lib/otPasteGuard";
import { toast } from "sonner";

// COMPOSITION STRUCTURE MAP — a deterministic, Grade-9-simple picture of the organization of the whole
// (TWS's view of the canonical composition_hierarchy). Layout is DETERMINISTIC from the data (role bands +
// even spacing), computed in pixels — NOT from flex-wrap or DOM measurement — so viewport width changes SCALE/
// SCROLL only, never the apparent organization. Student-facing edge grammar (4 meanings): ordinary connection
// (grey), you-worked-this-out (emerald), we're-working-here (brand highlight), open connection (amber "?").
const ROLE_STYLE = {
  thesis: "bg-[#8C3A2A] text-white border-[#8C3A2A]",
  big_idea: "bg-amber-100 text-amber-900 border-amber-300",
  part: "bg-white text-stone-800 border-stone-300",
  orienting: "bg-sky-50 text-sky-900 border-sky-200",
  closing: "bg-violet-50 text-violet-900 border-violet-200",
};
const NODE_W = 150, NODE_H = 46, MARGIN = 90;
const ROW_Y = { thesis: 8, orienting: 86, part: 170, closing: 252 };
const bandOf = (kind) => (kind === "thesis" ? "thesis" : kind === "orienting" ? "orienting" : kind === "closing" ? "closing" : "part");

const StructureMap = ({ map, compact = false, onCorrect, selectedUnits = null, correctPanel = null, onPick = null, onFocusWorkspace = null }) => {
  const nodes = map?.nodes || [];
  const nodeById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const focus = map?.focus || {};
  const probe = (focus.stage || "") === "probe";
  const selIds = (probe && selectedUnits && selectedUnits.length) ? selectedUnits.map((i) => `u${i}`) : null;

  const emphSet = useMemo(() => {
    const s = new Set();
    if (selIds) selIds.forEach((id) => s.add(id));
    else nodes.forEach((n) => { if (n.emphasis) s.add(n.id); });
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedUnits]);

  const { pos, W, H, edges } = useMemo(() => {
    const bands = { thesis: [], orienting: [], part: [], closing: [] };
    nodes.forEach((n) => bands[bandOf(n.kind)].push(n));
    const bandW = Math.max(bands.part.length, 1);
    const natW = Math.max(300, bands.part.length * (NODE_W + 24) + 40);
    const pos = {};
    const place = (arr, y) => {
      const n = arr.length;
      arr.forEach((node, i) => { pos[node.id] = { x: MARGIN + (i + 0.5) * (natW - 2 * MARGIN) / n, y: y + NODE_H / 2 }; });
    };
    // §3: while INTRODUCTION↔THESIS is active, the intro node reads ABOVE the thesis (rhetorical order).
    const introFirst = !!focus.intro_above_thesis && bands.orienting.length && bands.thesis.length;
    const thesisY = introFirst ? ROW_Y.orienting : ROW_Y.thesis;
    const orientingY = introFirst ? ROW_Y.thesis : ROW_Y.orienting;
    if (bands.thesis.length) pos[bands.thesis[0].id] = { x: natW / 2, y: thesisY + NODE_H / 2 };
    if (bands.orienting.length) place(bands.orienting, orientingY);
    if (bands.part.length) place(bands.part, ROW_Y.part);
    if (bands.closing.length) place(bands.closing, ROW_Y.closing);
    const lastY = bands.closing.length ? ROW_Y.closing : bands.part.length ? ROW_Y.part : ROW_Y.orienting;
    const base = map?.edges || [];
    let es = base;
    if (probe) es = (selIds && selIds.length >= 2) ? [{ source: selIds[0], target: selIds[1], kind: "open", emphasis: true }] : base.filter((e) => e.kind === "open");
    const segs = es.map((e, i) => {
      const a = pos[e.source], b = pos[e.target];
      if (!a || !b) return null;
      return { ...e, x1: a.x, y1: a.y, x2: b.x, y2: b.y, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, key: `${e.source}-${e.target}-${i}` };
    }).filter(Boolean);
    return { pos, W: natW, H: lastY + NODE_H + 10, edges: segs, bandW };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedUnits]);

  if (!nodes.length) return null;
  const isEmph = (id) => emphSet.has(id);
  const strokeFor = (s) => (s.emphasis ? "#8C3A2A" : s.kind === "constructed" ? "#059669" : s.kind === "open" ? "#d97706" : "#78716c");
  const widthFor = (s) => (s.emphasis ? 3 : s.kind === "constructed" ? 2.5 : 1.6);
  const dashFor = (s) => ((s.kind === "open" || s.status === "needs_work") ? "5 4" : undefined);
  const hasConstructed = edges.some((s) => s.kind === "constructed");
  const hasOpen = edges.some((s) => s.kind === "open");
  const hasNeeds = edges.some((s) => s.status === "needs_work");
  const hasWorking = edges.some((s) => s.status === "working");

  return (
    <div data-testid="essay-structure-map" className="rounded-lg border border-stone-200 bg-stone-50/70 px-3 py-2.5">
      <div className="flex items-center justify-between mb-1">
        <p className="font-mono text-[8.5px] uppercase tracking-wider text-stone-500">How your essay is organized</p>
        {onCorrect && (
          <button data-testid="essay-map-correct" onClick={onCorrect} className="text-[10px] text-stone-500 hover:text-[#8C3A2A] underline decoration-dotted">That's not how it's organized</button>
        )}
      </div>
      {focus.hint && (
        <p data-testid="essay-map-focus" className="mb-1.5 text-[10.5px] font-medium text-[#8C3A2A]">{focus.hint}</p>
      )}
      <div className="overflow-x-auto">
        <div className="relative mx-auto" style={{ width: W, height: H }}>
          <svg className="absolute inset-0" width={W} height={H} style={{ zIndex: 0 }}>
            {edges.map((s) => (
              <g key={s.key}>
                <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={strokeFor(s)} strokeWidth={widthFor(s)}
                  strokeDasharray={dashFor(s)} strokeLinecap="round" style={{ pointerEvents: "none" }}
                  opacity={s.kind === "belongs_to" && s.visible === false ? 0.3 : 0.9} />
                {onPick && (
                  <line data-testid="essay-map-edge" x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke="transparent"
                    strokeWidth="16" style={{ pointerEvents: "stroke", cursor: "pointer" }}
                    onClick={() => onPick([...((nodeById[s.source] || {}).excerpts || []), ...((nodeById[s.target] || {}).excerpts || [])])} />
                )}
                {s.kind !== "open" && s.label && (
                  <text data-testid="essay-map-edge-label" x={s.mx} y={s.my} textAnchor="middle" dominantBaseline="central"
                    fontSize="8" fill={s.kind === "constructed" ? "#047857" : "#78716c"} style={{ pointerEvents: "none" }}
                    stroke="#ffffff" strokeWidth="2.5" paintOrder="stroke">{s.label}</text>
                )}
                {s.kind === "open" && (
                  <>
                    <circle cx={s.mx} cy={s.my} r="11" fill="#fffbeb" stroke="#d97706" strokeWidth="1.8" style={{ pointerEvents: "none" }} />
                    <text x={s.mx} y={s.my + 5} textAnchor="middle" fontSize="15" fontWeight="800" fill="#d97706" style={{ pointerEvents: "none" }}>?</text>
                    {onFocusWorkspace && (
                      <circle data-testid="essay-map-open-q" cx={s.mx} cy={s.my} r="14" fill="transparent"
                        style={{ pointerEvents: "all", cursor: "pointer" }} onClick={onFocusWorkspace}>
                        <title>Work out this connection</title>
                      </circle>
                    )}
                  </>
                )}
              </g>
            ))}
          </svg>
          {nodes.map((n) => {
            const p = pos[n.id]; if (!p) return null;
            const em = isEmph(n.id);
            const isThesis = n.kind === "thesis";
            const w = isThesis ? Math.min(W - 20, 250) : NODE_W;
            const h = isThesis ? NODE_H + 30 : NODE_H;
            return (
              <div key={n.id} data-testid={n.kind === "thesis" ? "essay-map-thesis" : "essay-map-node"}
                title={n.full || n.label || ""}
                onClick={onPick ? () => onPick(n.excerpts || []) : undefined}
                className={`absolute flex items-center justify-center rounded-md border text-center px-2 leading-tight ${onPick ? "cursor-pointer hover:brightness-95" : ""} ${isThesis ? "font-semibold text-[11px]" : "text-[11.5px]"} ${em ? "ring-2 ring-[#8C3A2A]/60 shadow-sm " : ""}${ROLE_STYLE[n.kind] || ROLE_STYLE.part} ${n.realized === false && !em && !isThesis ? "opacity-70 border-dashed" : ""}`}
                style={{ left: p.x - w / 2, top: p.y - NODE_H / 2, width: w, height: h, zIndex: 1 }}>
                <span className={isThesis ? "line-clamp-4 overflow-hidden" : "line-clamp-2 overflow-hidden"}>{n.label || "(part)"}</span>
              </div>
            );
          })}
        </div>
      </div>
      {(hasConstructed || hasOpen || hasWorking || hasNeeds) && (
        <div data-testid="essay-map-legend" className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[9.5px] text-stone-500">
          {hasWorking && <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 bg-stone-500" /> working</span>}
          {hasNeeds && <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 border-t border-dashed border-stone-500" /> needs work</span>}
          {hasOpen && <span className="flex items-center gap-1"><span className="font-bold text-amber-600">?</span> we're working here</span>}
          {hasConstructed && <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 bg-emerald-600" /> you worked this out</span>}
        </div>
      )}
      {map.constructed_relation && !probe && (
        <p data-testid="essay-map-relation" className="mt-1.5 text-[10.5px] text-emerald-700">You worked out: <span className="font-medium">{map.constructed_relation}</span></p>
      )}
      {correctPanel}
    </div>
  );
};

// student's actual draft works as a WHOLE and surfaces ONE consequential next move. It routes DOWN to
// the existing local Composition engine when a problem is local, and BACK to My Thinking (local OT) when
// substantive meaning is missing. The machinery stays hidden — the student mostly sees their writing,
// one piece of dialogue, and one next action.
export default function EssayComposition({ sessionId, initialDraft = "", fromFsc = false, onLocalComposition, onBack, onReturnToThinking }) {
  const [loading, setLoading] = useState(true);
  const [continuation, setContinuation] = useState(false);          // PHASE C3 — just returned from OT recursion
  const [continuationCtx, setContinuationCtx] = useState(null);     // PHASE C3 — {writing_label, worked_out, right_now}
  const [assignment, setAssignment] = useState("");
  const [pathway, setPathway] = useState("full");
  const [thinking, setThinking] = useState({ items: [], questions: [], question_relationships: [] });
  const [draft, setDraft] = useState(initialDraft);
  const [checkedDraft, setCheckedDraft] = useState(initialDraft);  // draft as of the last real check → drives "dirty"
  const draftRef = useRef(null);
  const workspaceRef = useRef(null);
  const scrollToWorkspace = useCallback(() => {
    try { workspaceRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) { /* noop */ }
  }, []);

  // OBJECT ANCHORING: resolve a VERBATIM excerpt to a span in the current draft (deterministic substring match,
  // ellipsis-tolerant). Returns [start,end] or null — never guesses. Highlights My Writing via native selection.
  const resolveSpan = useCallback((excerpt) => {
    if (!excerpt) return null;
    const hay = draft.toLowerCase();
    let needle = String(excerpt).toLowerCase().replace(/[\u2026.]+\s*$/, "").trim();
    if (!needle) return null;
    let i = hay.indexOf(needle);
    if (i < 0 && needle.length > 24) i = hay.indexOf(needle.slice(0, 24));  // tolerate excerpt drift
    if (i < 0) { const first = needle.split(/\s+/).slice(0, 6).join(" "); if (first.length > 6) i = hay.indexOf(first); needle = first; }
    return i < 0 ? null : [i, i + needle.length];
  }, [draft]);

  const highlightExcerpts = useCallback((excerpts) => {
    const ta = draftRef.current; if (!ta) return;
    const spans = (excerpts || []).map(resolveSpan).filter(Boolean);
    if (!spans.length) { toast("I can't find that part in your current draft — it may have been revised."); return; }
    const start = Math.min(...spans.map((s) => s[0]));
    const end = spans.length === 1 || (Math.max(...spans.map((s) => s[1])) - start) <= 700
      ? Math.max(...spans.map((s) => s[1])) : spans[0][1];
    ta.focus(); ta.setSelectionRange(start, end);
    const ratio = start / Math.max(draft.length, 1);
    ta.scrollTop = Math.max(0, ratio * ta.scrollHeight - ta.clientHeight / 3);
    if (spans.length > 1 && end === spans[0][1]) toast(`Showing the first of ${spans.length} places this appears.`);
  }, [resolveSpan, draft]);
  const [tws, setTws] = useState({ message: "", level: "S0", routing: "" });
  const [agenda, setAgenda] = useState([]);            // structural targets: [{key,description,status,...}]
  const [activeKey, setActiveKey] = useState("");      // the target currently being worked
  const [episode, setEpisode] = useState({ active: false, phase: "" });   // developmental structural help episode
  const [roadmapOpen, setRoadmapOpen] = useState(false);   // roadmap collapsed by default
  const [mapCorrectOpen, setMapCorrectOpen] = useState(false);
  const [workedOutOpen, setWorkedOutOpen] = useState(false);
  const [probeText, setProbeText] = useState("");
  const [relateText, setRelateText] = useState("");
  const [selUnits, setSelUnits] = useState([]);
  const [notQuiteOpen, setNotQuiteOpen] = useState(false);
  const [notQuiteText, setNotQuiteText] = useState("");
  const [goNoteOpen, setGoNoteOpen] = useState(false);
  const [goNoteText, setGoNoteText] = useState("");
  const [selForm, setSelForm] = useState("");
  const [busy, setBusy] = useState(false);
  const [initializing, setInitializing] = useState(true); // true until the FIRST composition analysis completes
  const [pendingAction, setPendingAction] = useState(""); // which action is processing (shared loading state)
  const [turnError, setTurnError] = useState("");
  const [analysisFailed, setAnalysisFailed] = useState(false); // an analysis/reconstruction request failed → show retry, never instructional controls
  const [intention, setIntention] = useState("");
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const [newIdea, setNewIdea] = useState("");
  const [savedNote, setSavedNote] = useState(false);
  const [passBWorkOpen, setPassBWorkOpen] = useState(true);   // student's own prose, visible throughout Pass B
  const [showStructureMap, setShowStructureMap] = useState(false);  // §8: full functional diagram is opt-in, hidden by default
  const saveTimer = useRef(null);
  const explainRef = useRef(null);

  const runTurn = useCallback(async (body) => {
    setBusy(true);
    setPendingAction(body?.mode || "primary");
    setTurnError("");
    setAnalysisFailed(false);
    try {
      const r = await essayTurn(sessionId, { draft, ...body });
      if (body?.mode !== "silent_save") setCheckedDraft(body?.draft ?? draft);   // this draft is now "checked"
      setTws({ message: r.message, level: r.support_level, routing: r.routing, descent: r.descent, structure_map: r.structure_map, confirmation: r.confirmation, global_org: r.global_org, thought_parts: r.thought_parts, active_resource_part: r.active_resource_part });
      if (r.continuation !== undefined) { setContinuation(!!r.continuation); setContinuationCtx(r.continuation_context || null); }
      setAgenda(r.structural_agenda || []);
      setActiveKey(r.active_target_key || "");
      setEpisode(r.episode || { active: false, phase: "" });
      if ((r.episode || {}).selected_units) setSelUnits(r.episode.selected_units);
      if (r.routing === "reopen_thinking") setThinkingOpen(true);
    } catch (e) {
      // A reconstruction/reanalysis request that fails must surface an explicit retry — never fall through to
      // the normal instructional controls (that is what produced the "One moment…" + stray button hang).
      if (body?.mode === "recheck") setAnalysisFailed(true);
      else setTurnError("Something went wrong checking your writing. Please try again.");
    } finally { setBusy(false); setPendingAction(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, draft]);

  const runInitialAnalysis = useCallback(async (d) => {
    setBusy(true);
    setAnalysisFailed(false);
    try {
      const r = await essayTurn(sessionId, { draft: d });
      // §4 — OT→Refinement transfer: if the backend transferred the student's OWN authored prose into My
      // Writing (rich Route A), adopt it so the workspace is NOT blank. Only when we sent an empty draft.
      const seeded = (!d || !d.trim()) && typeof r.draft === "string" && r.draft.trim();
      if (seeded) { setDraft(r.draft); setCheckedDraft(r.draft); } else { setCheckedDraft(d); }
      setTws({ message: r.message, level: r.support_level, routing: r.routing, descent: r.descent, structure_map: r.structure_map, confirmation: r.confirmation, global_org: r.global_org, thought_parts: r.thought_parts, active_resource_part: r.active_resource_part });
      if (r.continuation !== undefined) { setContinuation(!!r.continuation); setContinuationCtx(r.continuation_context || null); }
      setAgenda(r.structural_agenda || []);
      setActiveKey(r.active_target_key || "");
      setEpisode(r.episode || { active: false, phase: "" });
      if (r.routing === "reopen_thinking") setThinkingOpen(true);
    } catch (e) {
      // An EMPTY draft has nothing to read — a failed initial fetch here is not a real "couldn't read
      // your writing" failure. Show the neutral write-your-response state instead of a scary retry card.
      if (d && d.trim()) {
        setAnalysisFailed(true);   // real draft failed to read → retry card, no instructional controls
      } else {
        setTws({ message: "Start writing your response below whenever you're ready — I'll read it with you as you go.", level: "S0", routing: "" });
      }
    } finally { setBusy(false); setInitializing(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    (async () => {
      if (!sessionId) {
        setLoading(false);
        setTws({ message: "No writing session yet — open Composition from the Student Portal to begin.", level: "S0", routing: "" });
        return;
      }
      let s;
      try {
        s = await essayState(sessionId);
      } catch (e) {
        setLoading(false);
        setTws({ message: "This session couldn't be loaded. Return to the Student Portal and start again.", level: "S0", routing: "" });
        return;
      }
      setAssignment(s.assignment || "");
      setPathway(s.pathway || "full");
      setThinking(s.thinking || { items: [], questions: [], question_relationships: [] });
      setContinuation(!!s.continuation);
      setContinuationCtx(s.continuation_context || null);
      const d = initialDraft || s.draft || "";
      setDraft(d);
      setLoading(false);
      await runInitialAnalysis(d);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDraftChange = (val) => {
    setDraft(val);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { essayTurn(sessionId, { draft: val, mode: "silent_save" }).catch(() => {}); }, 1200);
  };

  // AUTOMATIC OBJECT ANCHORING: when the active routine step changes, highlight its prose so TWS establishes
  // the shared object of attention (the student should not have to ask "which paragraph?").
  const goAnchorRef = (tws.global_org && tws.global_org.step && tws.global_org.step.ref) || "";
  useEffect(() => {
    const g = tws.global_org;
    if (g && g.active && g.step && (g.step.excerpts || []).length) {
      const t = setTimeout(() => highlightExcerpts(g.step.excerpts), 300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goAnchorRef]);

  const saveThinking = async () => {
    const t = newIdea.trim();
    if (!t) return;
    await essaySaveThinking(sessionId, t).catch(() => {});
    // reflect it immediately in the local My Thinking record (verbatim, student-authored)
    setThinking((prev) => ({ ...prev, items: [...(prev.items || []), { id: `local-${Date.now()}`, text: t, source: "student", origin: "composition" }] }));
    setNewIdea("");
    setSavedNote(true);
    setTimeout(() => setSavedNote(false), 2500);
  };

  // PHASE C2 — the student performs the reader-order judgment (which developed part the reader meets next).
  const chooseReaderPart = async (partId) => {
    if (busy) return;
    setBusy(true); setPendingAction("reader-order");
    try {
      const r = await essayReaderOrder(sessionId, partId);
      setTws({ message: r.message, level: r.support_level, routing: r.routing, descent: r.descent, structure_map: r.structure_map, confirmation: r.confirmation, global_org: r.global_org, thought_parts: r.thought_parts, active_resource_part: r.active_resource_part });
      if (r.continuation !== undefined) { setContinuation(!!r.continuation); setContinuationCtx(r.continuation_context || null); }
    } catch (e) { /* keep current view */ }
    finally { setBusy(false); setPendingAction(""); }
  };

  // PHASE C3 — writing revealed something worth working out: hand off to the SAME OT map (essay preserved).
  const returnToThinking = async (purpose = {}) => {
    if (busy) return;
    setBusy(true); setPendingAction("return-to-thinking");
    try {
      const r = await essayReturnToThinking(sessionId, purpose);
      onReturnToThinking?.(r.return_purpose);
    } catch (e) { /* stay */ }
    finally { setBusy(false); setPendingAction(""); }
  };


  if (loading) return <div className="min-h-screen grid place-items-center bg-[#faf9f6]"><Loader2 className="h-6 w-6 animate-spin text-[#8C3A2A]" /></div>;

  const routing = tws.routing;
  const descent = tws.descent || {};
  const structureMap = tws.structure_map || null;
  const inParagraph = routing === "paragraph_composition";
  const goLocal = () => onLocalComposition && onLocalComposition(draft);
  const sendCorrection = (correction) => runTurn({ mode: "structure_map_correct", correction });
  const isStructural = routing === "whole_structure" || routing === "major_unit";
  const confirm = tws.confirmation;   // dialogic confirmation payload (Increment 2)
  const go = tws.global_org || { active: false };   // Global Organization Routine sequencer view
  const goActive = !!go.active;
  const analyzing = busy && (initializing || pendingAction === "recheck");   // any active reconstruction → wait state
  const goStep = go.step || {};
  const dirty = draft !== checkedDraft;   // student edited the draft since the last real check
  const inEpisode = routing === "structural_episode";
  const atEndpoint = inEpisode && episode.phase === "endpoint";
  const inRealize = inEpisode && episode.phase === "realize";
  const inProbe = inEpisode && !atEndpoint && !inRealize;
  const attainedRel = (((episode.attained_organization || {}).relations) || []).slice(-1)[0];
  const attainedText = (attainedRel && attainedRel.relation) || (((episode.constructed_relations || []).slice(-1)[0] || {}).relation) || "";
  const realizeFns = episode.realization_functions || [];
  const curFn = realizeFns[episode.current_function];
  const proseDisp = episode.prose_disposition;
  const studentWork = episode.student_work || [];
  const activeItem = (agenda || []).find((a) => a.key === activeKey);
  const activeTitle = (activeItem && activeItem.title) || "";
  const resolvedCount = (agenda || []).filter((a) => a.status === "resolved").length;      // DONE — realized in the writing
  const openCount = (agenda || []).filter((a) => a.status === "open").length;               // TO STRENGTHEN — current open targets only
  const developingCount = (agenda || []).filter((a) => a.status === "met_for_now").length; // organization built, not yet in the writing

  const helpWork = () => runTurn({ mode: "help_work" });
  const toggleUnit = (idx) => setSelUnits((s) => (s.includes(idx) ? s.filter((i) => i !== idx) : (s.length >= 2 && episode.phase === "externalize" ? [s[1], idx] : [...s, idx])));
  const sendProbe = () => {
    if (!probeText.trim()) return;
    runTurn({ mode: "probe", probe_response: probeText.trim(), selected_units: selUnits, relation_form: selForm });
    setProbeText(""); setSelForm("");
  };

  // B1 — NEVER put a TWS dialogue response into the essay. The student's Pass-A responses are THEIR OWN THINKING WORDS: a RESOURCE they
  // may draw on while they revise My Writing themselves. AUTHORSHIP \u2260 COMPOSITIONAL INTENT: an answer to a TWS question is not essay
  // prose merely because the student wrote it. The old automatic append (`use_my_writing`) is gone; TWS does not perform the transfer.

  // real OT hierarchy, verbatim (read-only resource — not a blueprint)
  const questions = (thinking.questions || []).filter((q) => (q.source || "student") === "student");
  const qrels = thinking.question_relationships || [];
  const items = thinking.items || [];
  const primaryQ = (i) => (i.question_ids || []).find((qid) => questions.some((q) => q.id === qid));
  const directIdeas = (qid) => items.filter((i) => !i.parent_id && primaryQ(i) === qid);
  const looseIdeas = items.filter((i) => !i.parent_id && !primaryQ(i));

  // Reusable correction affordance for the persistent Structure Map (student provenance; TWS never invents).
  const mapCorrectPanel = (mapCorrectOpen && structureMap) ? (
    <div data-testid="essay-map-correct-panel" className="mt-2 rounded-md border border-stone-200 bg-white px-2.5 py-2">
      <p className="text-[10.5px] text-stone-500 mb-1.5">Tell me where I've got it wrong — does each part fit your main idea?</p>
      <div className="flex flex-col gap-1">
        {(structureMap.nodes || []).filter((n) => n.kind === "part" || n.kind === "big_idea").map((n) => {
          const uid = parseInt(n.id.slice(1), 10);
          return (
            <div key={n.id} className="flex items-center justify-between gap-2">
              <span className="text-[11.5px] text-stone-700 truncate">{n.label}</span>
              <span className="flex gap-1 shrink-0">
                <button data-testid="essay-map-fits" onClick={() => sendCorrection({ action: "set_relation_to_thesis", unit: uid, value: "clear" })} disabled={busy}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${n.realized ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-stone-300 text-stone-500"}`}>fits</button>
                <button data-testid="essay-map-notfit" onClick={() => sendCorrection({ action: "set_relation_to_thesis", unit: uid, value: "none" })} disabled={busy}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${!n.realized ? "bg-amber-50 border-amber-300 text-amber-800" : "border-stone-300 text-stone-500"}`}>doesn't fit</button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;
  // §8 — MY WRITING is primary. The full functional StructureMap is NOT shown by default; it is opt-in via a
  // small toggle (available whenever a map exists), so the learner sees only the structure needed for the
  // current act, not the entire internal model of the essay. The underlying functional model is untouched.
  const mapAvailable = structureMap && (structureMap.nodes || []).length > 0 && (isStructural || inEpisode || goActive);
  const showMap = mapAvailable && showStructureMap;
  const StructureMapToggle = () => (
    <button data-testid="essay-toggle-structure-map" onClick={() => setShowStructureMap((v) => !v)}
      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-stone-500 hover:text-[#8C3A2A] border border-stone-200 rounded-full px-2.5 py-1 transition-colors">
      <GitBranch className="h-3 w-3" /> {showStructureMap ? "Hide structure map" : "Show structure map"}
    </button>
  );

  return (
    <div data-testid="essay-root" className="min-h-screen bg-[#faf9f6] text-stone-900">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5">
        <div className="mb-4 flex items-start gap-3">
          <button data-testid="essay-back" onClick={() => onBack && onBack(fromFsc)}
            className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-[#8C3A2A]">
            ← Back
          </button>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A]">The Writing Studio · Composition</p>
            <p data-testid="essay-assignment" className="text-[13px] text-stone-600 mt-1 max-w-3xl">{assignment}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5">
          {/* MY WRITING — the whole draft, editable in place */}
          <section data-testid="essay-writing" className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <PenLine className="h-4 w-4 text-[#8C3A2A]" />
              <h2 className="font-serif text-lg text-stone-900">My Writing</h2>
            </div>
            <textarea data-testid="essay-draft" {...otNoPaste} value={draft} ref={draftRef}
              onChange={(e) => onDraftChange(e.target.value)}
              placeholder="Your draft…"
              className="w-full min-h-[420px] text-[15px] leading-relaxed p-3 rounded-lg border border-stone-200 outline-none focus:border-[#8C3A2A] resize-y" />
          </section>

          {/* RIGHT: TWS whole-first dialogue + My Thinking access */}
          <div className="space-y-5">
            <section data-testid="essay-tws" className="rounded-2xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquareText className="h-4 w-4 text-[#8C3A2A]" />
                <h2 className="font-serif text-lg text-stone-900">The Writing Studio</h2>
                {busy && (
                  <span data-testid="essay-processing" className="ml-auto inline-flex items-center gap-2 text-[#8C3A2A]">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-[13px] font-semibold tracking-tight">Thinking…</span>
                  </span>
                )}
                {!busy && tws.level && tws.level !== "S0" && <span data-testid="essay-support-level" className="ml-auto text-[9px] font-mono uppercase tracking-wider text-stone-300">{tws.level}</span>}
              </div>
              {/* WHAT YOUR DRAFT STILL NEEDS — the roadmap comes FIRST so the instruction below (CURRENT WORK)
                  sits directly against the activity it governs. COLLAPSED by default; never shows dev levels. */}
              {agenda.length > 0 && (
                <div data-testid="essay-draft-needs" className="mb-3 rounded-lg border border-amber-300/70 bg-amber-50/60 px-3.5 py-2.5">
                  <button data-testid="essay-roadmap-toggle" onClick={() => setRoadmapOpen((o) => !o)}
                    className="w-full flex items-center gap-1.5 text-left">
                    <GitBranch className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                    <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-amber-800 flex-1">
                      What your draft still needs — {openCount} to strengthen{developingCount > 0 ? ` · ${developingCount} in progress` : ""}
                    </p>
                    <span className="text-[10px] font-medium text-amber-700">{roadmapOpen ? "Hide" : "View all"}</span>
                  </button>
                  {roadmapOpen && (
                    <ol className="mt-2 space-y-1">
                      {agenda.map((t, i) => {
                        const active = t.key === activeKey && t.status === "open";
                        const done = t.status === "met_for_now" || t.status === "resolved";
                        const label = t.title || t.description || "A part of the structure still needs work.";
                        return (
                          <li key={t.key}
                            data-testid={`essay-need-${t.status === "met_for_now" ? "metfornow" : t.status === "resolved" ? "resolved" : active ? "active" : "queued"}`}
                            className={`flex items-baseline gap-2 text-[12.5px] leading-snug ${
                              active ? "text-stone-900 font-semibold" : done ? "text-emerald-700/80" : "text-stone-500"}`}>
                            <span className="shrink-0 tabular-nums w-4 text-right">
                              {done ? <Check className="h-3.5 w-3.5 inline text-emerald-600" /> : `${i + 1}.`}
                            </span>
                            <span className="flex-1">{label}
                              {active && <span className="ml-2 text-[8.5px] font-mono uppercase tracking-wider text-amber-700 align-middle">← working on this</span>}
                              {t.status === "met_for_now" && <span className="ml-2 text-[10px] text-emerald-600">— relationship worked out</span>}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}

              {/* WAIT / FAILURE — replaces the workspace while an analysis request is in flight or has failed. */}
              {(analyzing || analysisFailed) && (
                <div className="rounded-lg border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] px-3.5 py-2.5">
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C3A2A]/80 mb-0.5">Current work</p>
                  {analysisFailed ? (
                    <div data-testid="essay-analysis-failed">
                      <p className="text-[13.5px] font-semibold leading-relaxed text-stone-900">I couldn't finish reading your writing this time.</p>
                      <p className="text-[12px] text-stone-600 mt-0.5">This can happen with a long draft. Let's try once more.</p>
                      <button data-testid="essay-analysis-retry" onClick={() => runInitialAnalysis(draft)} disabled={busy}
                        className="mt-2 inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
                        {busy ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading…</>) : (<><RefreshCw className="h-3.5 w-3.5" /> Try again</>)}
                      </button>
                    </div>
                  ) : (
                    <div data-testid="essay-analyzing">
                      <p className="text-[13.5px] font-semibold leading-relaxed text-stone-900">TWS is reading your writing</p>
                      <p className="text-[12.5px] leading-relaxed text-stone-700 mt-0.5">I'm looking at how the parts of your paper work together. Please wait — you don't need to do anything yet.</p>
                      <p className="mt-1.5 inline-flex items-center gap-2 text-[#8C3A2A]"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-[12.5px] font-medium">Thinking…</span></p>
                    </div>
                  )}
                </div>
              )}

              {/* NON-GO (legacy structural flow / plain message) — its own message card plus the shared map. */}
              {!goActive && !analyzing && !analysisFailed && (
                <>
                  <div className="rounded-lg border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] px-3.5 py-2.5">
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C3A2A]/80 mb-0.5">Current work</p>
                    {isStructural && activeTitle && (
                      <p data-testid="essay-active-title" className="text-[15px] font-semibold leading-snug text-stone-900 mb-1">{activeTitle}</p>
                    )}
                    <p data-testid="essay-message" className="text-[13.5px] leading-relaxed text-stone-700 min-h-[36px]">{tws.message}</p>
                  </div>
                  {mapAvailable && (
                    <div className="mt-3">
                      <div className="flex justify-end mb-1.5"><StructureMapToggle /></div>
                      {showMap && (
                        <StructureMap map={structureMap} compact selectedUnits={inProbe ? selUnits : null}
                          onPick={highlightExcerpts} onCorrect={() => setMapCorrectOpen((o) => !o)} correctPanel={mapCorrectPanel} />
                      )}
                    </div>
                  )}
                </>
              )}

              {/* GLOBAL ORGANIZATION ROUTINE — a recognizable, repeatable learning routine over the SAME
                  hierarchy + map. The current step dominates; the whole sequence stays visible but secondary. */}
              {/* Thesis-first is the DEFAULT global-organization experience — no opt-in gateway button. */}
              {/* PHASE C3 — active field ON RETURN from OT recursion: orient the learner (you're writing X /
                  you just worked out Y / right now: put it into words). Independent of GO state. New OT thinking
                  is surfaced as a resource — never auto-inserted; the learner writes in the essay surface. */}
              {continuation && continuationCtx && !analyzing && !analysisFailed && (
                <div data-testid="essay-return-field" className="mt-3 rounded-lg border border-sky-300 bg-sky-50/70 px-3.5 py-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-sky-700 mb-1">Back to your writing</p>
                  {continuationCtx.writing_label ? (
                    <p className="text-[12.5px] text-stone-800"><span className="text-stone-500">You&rsquo;re writing:</span> <span className="font-semibold">{continuationCtx.writing_label}</span></p>
                  ) : null}
                  {continuationCtx.worked_out ? (
                    <p className="text-[12.5px] text-stone-800 mt-1"><span className="text-stone-500">You just worked out:</span> {continuationCtx.worked_out}</p>
                  ) : null}
                  <p data-testid="essay-return-right-now" className="text-[13px] font-medium text-stone-900 mt-2">{continuationCtx.right_now}</p>
                </div>
              )}
              {goActive && !analyzing && !analysisFailed && (
                <div data-testid="essay-go" className="mt-3 space-y-3">
                  {/* PHASE B — developmental handoff: orient the learner to the new task (turn thinking into
                      writing) and surface the STUDENT-OWNED working thesis. Shown on OT entry, early phases
                      only. No introduction teaching here (that is C1). */}
                  {fromFsc && !continuation && (go.phase === "whole" || go.phase === "introduction") && (go.thesis_ref || go.thesis_quote) && (
                    <div data-testid="essay-phaseb-handoff" className="rounded-lg border border-emerald-300 bg-emerald-50/70 px-3.5 py-3">
                      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-emerald-700 mb-1">From thinking to writing</p>
                      <p className="text-[13.5px] text-stone-800 leading-relaxed">You've developed and organized your thinking. Now we're going to turn that thinking into an essay for a reader.</p>
                      <div className="mt-2.5 rounded-md border-l-2 border-[#8C3A2A] bg-white/70 pl-3 py-1.5">
                        <p className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-[#8C3A2A]/80">Your working thesis</p>
                        <p data-testid="essay-phaseb-thesis" className="text-[13.5px] font-medium text-stone-900 leading-snug mt-0.5">{go.thesis_ref || go.thesis_quote}</p>
                      </div>
                      <p className="text-[11.5px] text-stone-500 mt-2">You can still change this as your writing develops.</p>
                    </div>
                  )}
                  {/* PHASE C2 — THOUGHT MAP ⇄ COMMUNICATIVE ORGANIZATION: a BOUNDED active field of the
                      student's developed major thought parts + the reader-order communicative judgment. Parts
                      are RESOURCE (verbatim, student-owned) — never inserted into prose; the learner writes in
                      the essay surface. */}
                  {tws.thought_parts && (tws.thought_parts.parts || []).length > 0 && (
                    <div data-testid="essay-thought-parts" className="rounded-lg border border-amber-300 bg-amber-50/60 px-3.5 py-3">
                      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-amber-700 mb-1">What your reader needs next</p>
                      <p data-testid="essay-reader-order-prompt" className="text-[13.5px] text-stone-800 leading-relaxed">{tws.thought_parts.prompt}</p>
                      <div className="mt-3 space-y-2">
                        {(tws.thought_parts.parts || []).map((p) => (
                          <div key={p.id} data-testid={`thought-part-${p.id}`} className="rounded-md border border-stone-200 bg-white px-3 py-2">
                            {p.label ? <p className="text-[12.5px] font-semibold text-stone-900 leading-snug">{p.label}</p> : null}
                            <p className="text-[12.5px] text-stone-700 leading-snug mt-0.5">{p.text}</p>
                            {(p.children || []).length > 0 && (
                              <ul className="mt-1 pl-3 list-disc marker:text-stone-300 space-y-0.5">
                                {p.children.map((c, ci) => <li key={ci} className="text-[11.5px] text-stone-500 leading-snug">{c}</li>)}
                              </ul>
                            )}
                            <button data-testid={`thought-part-choose-${p.id}`} disabled={busy}
                              onClick={() => chooseReaderPart(p.id)}
                              className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-[#8C3A2A] hover:text-[#6B2C20] disabled:opacity-50">
                              <ArrowRight className="h-3.5 w-3.5" /> My reader needs this first
                            </button>
                          </div>
                        ))}
                      </div>
                      {tws.thought_parts.quiet_count > 0 && (
                        <p className="text-[11px] text-stone-400 mt-2">+{tws.thought_parts.quiet_count} more part{tws.thought_parts.quiet_count > 1 ? "s" : ""} set aside for now.</p>
                      )}
                      <p className="text-[11px] text-stone-500 mt-2 italic">{tws.thought_parts.resource_note}</p>
                    </div>
                  )}
                  {tws.active_resource_part && (
                    <div data-testid="essay-active-resource" className="rounded-lg border border-stone-200 bg-white px-3.5 py-3">
                      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500 mb-1">You developed these ideas</p>
                      {tws.active_resource_part.label ? <p className="text-[12.5px] font-semibold text-stone-900">{tws.active_resource_part.label}</p> : null}
                      <p className="text-[12.5px] text-stone-700 leading-snug mt-0.5">{tws.active_resource_part.text}</p>
                      {(tws.active_resource_part.children || []).length > 0 && (
                        <ul className="mt-1 pl-3 list-disc marker:text-stone-300 space-y-0.5">
                          {tws.active_resource_part.children.map((c, ci) => <li key={ci} className="text-[11.5px] text-stone-500 leading-snug">{c}</li>)}
                        </ul>
                      )}
                      <p className="text-[11px] text-stone-500 mt-2 italic">A resource for your writing — put the larger point in your own words in the essay above.</p>
                    </div>
                  )}
                  <div data-testid="essay-go-routine" className="rounded-lg border border-[#8C3A2A]/20 bg-[#8C3A2A]/[0.03] px-3 py-2">
                    <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#8C3A2A]/80 mb-1.5">Organizing your paper</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(go.phases || []).map((p) => (
                        <span key={p.key} data-testid={`essay-go-phase-${p.key}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            p.status === "current" ? "bg-[#8C3A2A] text-white border-[#8C3A2A]"
                            : p.status === "done" ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                            : "border-stone-200 text-stone-400"}`}>
                          {p.label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* THE ORGANIZATIONAL MAP — the shared visual object for the current relationship (§5): the
                      two nodes + the ? on their edge. Clicking ? scrolls to the workspace; clicking a node
                      highlights its verbatim prose in My Writing. */}
                  {mapAvailable && (
                    <div data-testid="essay-go-map">
                      <div className="flex justify-end mb-1.5"><StructureMapToggle /></div>
                      {showMap && (
                        <StructureMap map={structureMap} compact selectedUnits={inProbe ? selUnits : null}
                          onPick={highlightExcerpts} onFocusWorkspace={scrollToWorkspace}
                          onCorrect={() => setMapCorrectOpen((o) => !o)} correctPanel={mapCorrectPanel} />
                      )}
                    </div>
                  )}

                  {!(goStep.relationship && (go.stage === "relate" || go.stage === "realize")) && go.orientation && (((go.orientation.working || []).length > 0) || ((go.orientation.developed || []).length > 0)) && (
                    <div data-testid="essay-go-orientation" className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-3.5 py-2.5 space-y-1">
                      {(go.orientation.working || []).length > 0 && (
                        <div data-testid="essay-go-working" className="text-[12.5px] text-stone-700 leading-snug">
                          <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-emerald-800">What's working</span>
                          <ul className="mt-0.5 space-y-0.5">
                            {go.orientation.working.map((w, i) => (
                              <li key={i} className="flex gap-1.5"><span className="text-emerald-600">•</span><span>{w}.</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(go.orientation.developed || []).length > 0 && (
                        <p data-testid="essay-go-developed" className="text-[12.5px] text-stone-700 leading-snug">
                          <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-emerald-800 mr-1.5">What you developed</span>
                          {go.orientation.developed.join("; ")}
                        </p>
                      )}
                    </div>
                  )}

                  {/* PERSISTENT WORKING-THESIS REFERENCE — keeps "this is what I'm trying to show" in view
                      across every global phase; Reconsider re-opens it without erasing completed work. */}
                  {go.thesis_ref && goStep.kind !== "whole" && (
                    <div data-testid="essay-go-thesis-ref" className="rounded-lg border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] px-3 py-2">
                      <p className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-[#8C3A2A]/80">Your working thesis</p>
                      {go.thesis_quote ? (
                        <p className="text-[12.5px] text-stone-800 leading-snug mt-0.5 italic">"{go.thesis_quote}"</p>
                      ) : (
                        <p className="text-[12.5px] text-stone-800 leading-snug mt-0.5">{go.thesis_ref}</p>
                      )}
                      <button data-testid="essay-go-reconsider" disabled={busy} onClick={() => runTurn({ mode: "global_org_reconsider" })}
                        className="mt-1 text-[10.5px] text-[#8C3A2A] underline decoration-dotted hover:text-[#6B2C20] disabled:opacity-50">Reconsider my thesis</button>
                    </div>
                  )}

                  <div ref={workspaceRef} data-testid="essay-go-step" className="rounded-lg border border-sky-200 bg-sky-50/60 px-3.5 py-3">
                    <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-sky-800/80 mb-0.5">Current work</p>
                    <p data-testid="essay-go-header" className="font-serif text-[16px] text-stone-900 leading-tight">{goStep.header}</p>
                    {goStep.subhead && <p className="text-[11.5px] text-stone-500 mt-0.5">{goStep.subhead}</p>}

                    {go.stage === "confirm" && (
                      <div className="mt-2" data-testid="essay-go-confirm">
                        {goStep.lead && <p className="text-[13px] text-stone-800 leading-relaxed"><span className="text-sky-700 font-medium">{goStep.lead}</span></p>}
                        <p data-testid="essay-go-interpretation" className="text-[13.5px] text-stone-900 leading-relaxed mt-0.5">{goStep.interpretation}</p>
                        {goStep.needs_realization && (
                          <p data-testid="essay-go-missing" className="mt-1.5 text-[12.5px] text-stone-700 leading-snug"><span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-amber-800 mr-1.5">Not clear yet</span>I can't yet see clearly how this connects to your thesis — that's what we'll work out.</p>
                        )}
                        {!goNoteOpen ? (
                          <>
                            <p className="mt-2 text-[12.5px] font-medium text-stone-700">{goStep.question}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {goStep.thesis_state !== "absent" && (
                                <button data-testid="essay-go-yes" disabled={busy} onClick={() => runTurn({ mode: "global_org_confirm", choice: "yes" })}
                                  className="inline-flex items-center gap-1.5 bg-sky-600 text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-sky-700 disabled:opacity-60">Yes</button>
                              )}
                              <button data-testid="essay-go-notquite" disabled={busy} onClick={() => setGoNoteOpen(true)}
                                className="inline-flex items-center gap-1.5 border border-stone-300 text-stone-700 text-[13px] font-medium px-4 py-2 rounded-md hover:bg-stone-100 disabled:opacity-60">{goStep.thesis_state === "absent" ? "Tell me my main idea" : "Not quite"}</button>
                              <button data-testid="essay-go-unsure" disabled={busy} onClick={() => runTurn({ mode: "global_org_confirm", choice: "unsure" })}
                                className="inline-flex items-center gap-1.5 border border-stone-300 text-stone-700 text-[13px] font-medium px-4 py-2 rounded-md hover:bg-stone-100 disabled:opacity-60">{goStep.thesis_state === "absent" ? "Help me figure it out" : "I'm not sure"}</button>
                            </div>
                            {goStep.can_reopen_thesis && (
                              <p data-testid="essay-go-reopen-hint" className="mt-2 text-[11.5px] text-stone-600 leading-snug">
                                Looking at this part makes me wonder whether our working thesis still captures what your paper is trying to show.{" "}
                                <button data-testid="essay-go-reopen-thesis" disabled={busy} onClick={() => runTurn({ mode: "global_org_reconsider" })}
                                  className="text-[#8C3A2A] underline decoration-dotted hover:text-[#6B2C20] disabled:opacity-50">Reconsider the thesis</button>
                              </p>
                            )}
                          </>
                        ) : (
                          <div className="mt-2">
                            <p className="text-[12.5px] font-medium text-stone-700 mb-1">Okay. Tell me in your own words.</p>
                            <textarea data-testid="essay-go-note" {...otNoPaste} value={goNoteText} onChange={(e) => setGoNoteText(e.target.value)} rows={3}
                              className="w-full text-[13px] rounded-md border border-stone-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-300" placeholder="In your own words…" />
                            <div className="mt-1.5 flex gap-2">
                              <button data-testid="essay-go-note-submit" disabled={busy || !goNoteText.trim()}
                                onClick={() => { runTurn({ mode: "global_org_confirm", choice: "not_quite", note: goNoteText.trim() }); setGoNoteOpen(false); setGoNoteText(""); }}
                                className="inline-flex items-center gap-1.5 bg-sky-600 text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-sky-700 disabled:opacity-60">Save how I see it</button>
                              <button disabled={busy} onClick={() => setGoNoteOpen(false)} className="text-[12.5px] text-stone-500 hover:text-stone-700">Cancel</button>
                            </div>
                          </div>
                        )}
                        {goStep.confirm_status === "disputed" && goStep.confirm_note && !goNoteOpen && (
                          <p data-testid="essay-go-saved" className="mt-2 text-[11px] text-emerald-700">Saved how you see it: <span className="font-medium">{goStep.confirm_note}</span></p>
                        )}
                      </div>
                    )}

                    {go.stage === "relate" && goStep.relationship && (
                      <div className="mt-1" data-testid="essay-go-relate">
                        <div className="mt-2 space-y-1.5">
                          {goStep.relationship.working && (
                            <p data-testid="essay-go-rel-working" className="text-[12.5px] text-stone-700 leading-snug">
                              <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-emerald-800 mr-1.5">What's working in the relationship</span>{goStep.relationship.working}
                            </p>
                          )}
                          {goStep.relationship.gap && (
                            <p data-testid="essay-go-rel-gap" className="text-[13px] text-stone-900 leading-snug">
                              <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-amber-800 mr-1.5">What's not working in the relationship</span>{goStep.relationship.gap}.
                            </p>
                          )}
                        </div>
                        {goStep.relationship.job && (
                          <p data-testid="essay-go-rel-job" className="mt-2 text-[13px] text-stone-900 leading-snug">
                            <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-[#8C3A2A] mr-1.5">Your job here</span>{goStep.relationship.job}
                          </p>
                        )}
                        {goStep.relationship.question && (
                          <div className="mt-2.5 rounded-lg border border-sky-200 bg-white px-3 py-2.5">
                            <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-sky-800">Think it through</p>
                            <p data-testid="essay-go-rel-question" className="mt-1 text-[13.5px] font-medium text-stone-900 leading-relaxed">{goStep.relationship.question}</p>
                          </div>
                        )}
                        <textarea data-testid="essay-go-relate-input" {...otNoPaste} value={relateText} onChange={(e) => setRelateText(e.target.value)} rows={3}
                          placeholder="Work it out in your own words…"
                          className="mt-2 w-full text-[13px] p-2.5 rounded-md border border-stone-200 outline-none focus:border-[#8C3A2A] resize-y" />
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <button data-testid="essay-go-relate-submit" disabled={busy || !relateText.trim()}
                            onClick={() => { runTurn({ mode: "global_org_relate", probe_response: relateText.trim() }); setRelateText(""); }}
                            className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
                            {pendingAction === "global_org_relate" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking with you…</>) : (<><ArrowRight className="h-3.5 w-3.5" /> Share my thinking</>)}
                          </button>
                          <button data-testid="essay-go-relate-works" disabled={busy} onClick={() => runTurn({ mode: "global_org_realize_works" })}
                            className="text-[12.5px] font-medium text-stone-600 hover:text-[#8C3A2A] disabled:opacity-50">This connection is already clear</button>
                        </div>
                        {goStep.reconsider_prompt && (
                          <div data-testid="essay-go-relate-reconsider" className="mt-2.5 rounded-lg border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] px-3 py-2">
                            <p className="text-[12px] text-stone-700 leading-snug">Working this out may change the idea organizing your whole paper. If your thesis needs to grow to fit it, you can revisit it.</p>
                            <button data-testid="essay-go-reconsider" disabled={busy} onClick={() => runTurn({ mode: "global_org_reconsider" })}
                              className="mt-1 text-[12px] text-[#8C3A2A] font-medium underline decoration-dotted hover:text-[#6B2C20] disabled:opacity-50">Reconsider my thesis</button>
                          </div>
                        )}
                      </div>
                    )}

                    {go.stage === "scaffold" && (
                      <div className="mt-2" data-testid="essay-go-scaffold">
                        <textarea data-testid="essay-go-probe-input" {...otNoPaste} value={probeText} onChange={(e) => setProbeText(e.target.value)} rows={3}
                          placeholder="Say what you think, in your own words…"
                          className="w-full text-[13px] p-2.5 rounded-md border border-stone-200 outline-none focus:border-[#8C3A2A] resize-y" />
                        <button data-testid="essay-go-probe-submit" disabled={busy || !probeText.trim()}
                          onClick={() => { runTurn({ mode: "global_org_probe", probe_response: probeText.trim() }); setProbeText(""); }}
                          className="mt-2 inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
                          {pendingAction === "global_org_probe" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking with you…</>) : (<><ArrowRight className="h-3.5 w-3.5" /> Share my thinking</>)}
                        </button>
                      </div>
                    )}

                    {go.stage === "realize" && (
                      <div className="mt-2" data-testid="essay-go-realize">
                        {goStep.relationship && (goStep.relationship.constructed || "").trim() && (
                          <p data-testid="essay-go-realize-lead" className="mb-2 text-[12.5px] text-stone-700 leading-snug">
                            <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-[#8C3A2A] mr-1.5">Now make it visible</span>
                            You worked out the connection (it's on your map now). Bring it into your writing where your reader needs it — in your own words.
                          </p>
                        )}
                        {goStep.reconsider_prompt && (
                          <div data-testid="essay-go-reconsider-prompt" className="mt-2 rounded-lg border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] px-3 py-2">
                            <p className="text-[12px] text-stone-700 leading-snug">What you worked out here may change the idea organizing your paper. Does your working thesis still say what you now mean?</p>
                            <button data-testid="essay-go-reconsider" disabled={busy} onClick={() => runTurn({ mode: "global_org_reconsider" })}
                              className="mt-1 text-[12px] text-[#8C3A2A] font-medium underline decoration-dotted hover:text-[#6B2C20] disabled:opacity-50">Reconsider the thesis</button>
                          </div>
                        )}
                        {go.check && go.check.status === "sufficient" && (
                          <div data-testid="essay-go-check-ok" className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                            <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-emerald-800">This part is working</p>
                            {go.check.working && <p className="text-[13px] text-stone-800 leading-relaxed mt-0.5">{go.check.working}</p>}
                            <button data-testid="essay-go-check-continue" disabled={busy} onClick={() => runTurn({ mode: "global_org_next" })}
                              className="mt-2 inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-60">Continue <ArrowRight className="h-3.5 w-3.5" /></button>
                          </div>
                        )}
                        {go.check && go.check.status === "insufficient" && (
                          <div data-testid="essay-go-check-gap" className="mb-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 space-y-1">
                            {go.check.working && <p className="text-[12.5px] text-stone-700 leading-snug"><span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-emerald-800 mr-1.5">Working</span>{go.check.working}</p>}
                            <p className="text-[13px] text-stone-900 leading-relaxed"><span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-amber-800 mr-1.5">Not yet</span>{go.check.gap}.</p>
                            {go.check.why && <p className="text-[12px] text-stone-600 leading-snug">{go.check.why}</p>}
                            {go.check.next && <p className="text-[12.5px] text-stone-800 leading-snug font-medium">{go.check.next}</p>}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <button data-testid="essay-go-realize-check" disabled={busy} onClick={() => runTurn({ mode: "global_org_realize_check" })}
                            className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-60">
                            {pendingAction === "global_org_realize_check" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>) : (<><Check className="h-3.5 w-3.5" /> {dirty || go.check ? "Check my revision" : "Check this part"}</>)}
                          </button>
                          <span className="inline-flex flex-col">
                            <button data-testid="essay-go-realize-works" disabled={busy} onClick={() => runTurn({ mode: "global_org_realize_works" })}
                              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-stone-700 hover:text-[#8C3A2A] disabled:opacity-50">Continue if you think this part is ready</button>
                            <span className="text-[10.5px] text-stone-400">Continue only if you're confident this part is doing the work you want it to do.</span>
                          </span>
                        </div>
                      </div>
                    )}

                    {go.stage === "checkpoint" && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="essay-go-checkpoint">
                        {goStep.kind === "reintegrate" ? (
                          <button data-testid="essay-go-reintegrate" disabled={busy} onClick={() => runTurn({ mode: "global_org_reintegrate" })}
                            className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-60">
                            {pendingAction === "global_org_reintegrate" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking…</>) : "Look at the whole again"}
                          </button>
                        ) : go.complete ? (
                          <button data-testid="essay-go-next" disabled={busy} onClick={() => runTurn({ mode: "global_org_next" })}
                            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-60">
                            {pendingAction === "global_org_next" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Setting up…</>) : (<>Work paragraph by paragraph <ArrowRight className="h-4 w-4" /></>)}
                          </button>
                        ) : (
                          <button data-testid="essay-go-next" disabled={busy} onClick={() => runTurn({ mode: "global_org_next" })}
                            className="inline-flex items-center gap-1.5 border border-stone-300 text-stone-700 text-[13px] font-medium px-4 py-2 rounded-md hover:bg-stone-100 disabled:opacity-60">Continue <ArrowRight className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* DIALOGIC CONFIRMATION — TWS shows what it's looking at, states its reading, and the student
                  confirms / corrects / is unsure. Disagreement is a normal, prominent choice (not hidden). */}
              {confirm && isStructural && !inEpisode && !goActive && ["unasked", "disputed"].includes(confirm.status) && (
                <div data-testid="essay-confirm" className="mt-3 rounded-lg border border-sky-300 bg-sky-50/70 px-3.5 py-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-sky-800 mb-1">We're working here</p>
                  <p className="text-[13.5px] text-stone-800 leading-relaxed"><span className="text-sky-700 font-medium">I read this as:</span> {confirm.interpretation}</p>
                  {confirm.excerpts?.length > 0 && (
                    <button data-testid="essay-confirm-show" onClick={() => highlightExcerpts(confirm.excerpts)}
                      className="mt-1 text-[11px] text-sky-700 underline decoration-dotted hover:text-sky-900">Show me in my writing</button>
                  )}
                  {!notQuiteOpen ? (
                    <>
                      <p className="mt-2 text-[12.5px] font-medium text-stone-700">{confirm.question}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button data-testid="essay-confirm-yes" disabled={busy} onClick={() => runTurn({ mode: "confirm_relation", choice: "yes" })}
                          className="inline-flex items-center gap-1.5 bg-sky-600 text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-sky-700 disabled:opacity-60">Yes</button>
                        <button data-testid="essay-confirm-notquite" disabled={busy} onClick={() => setNotQuiteOpen(true)}
                          className="inline-flex items-center gap-1.5 border border-stone-300 text-stone-700 text-[13px] font-medium px-4 py-2 rounded-md hover:bg-stone-100 disabled:opacity-60">Not quite</button>
                        <button data-testid="essay-confirm-unsure" disabled={busy} onClick={() => runTurn({ mode: "confirm_relation", choice: "unsure" })}
                          className="inline-flex items-center gap-1.5 border border-stone-300 text-stone-700 text-[13px] font-medium px-4 py-2 rounded-md hover:bg-stone-100 disabled:opacity-60">I'm not sure</button>
                      </div>
                    </>
                  ) : (
                    <div className="mt-2">
                      <p className="text-[12.5px] font-medium text-stone-700 mb-1">Okay. How do you see these ideas working together?</p>
                      <textarea data-testid="essay-confirm-note" {...otNoPaste} value={notQuiteText} onChange={(e) => setNotQuiteText(e.target.value)} rows={3}
                        className="w-full text-[13px] rounded-md border border-stone-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-300" placeholder="In your own words…" />
                      <div className="mt-1.5 flex gap-2">
                        <button data-testid="essay-confirm-note-submit" disabled={busy || !notQuiteText.trim()}
                          onClick={() => { runTurn({ mode: "confirm_relation", choice: "not_quite", note: notQuiteText.trim() }); setNotQuiteOpen(false); setNotQuiteText(""); }}
                          className="inline-flex items-center gap-1.5 bg-sky-600 text-white text-[13px] font-semibold px-4 py-2 rounded-md hover:bg-sky-700 disabled:opacity-60">Save how I see it</button>
                        <button disabled={busy} onClick={() => setNotQuiteOpen(false)}
                          className="text-[12.5px] text-stone-500 hover:text-stone-700">Cancel</button>
                      </div>
                    </div>
                  )}
                  {confirm.status === "disputed" && confirm.note && !notQuiteOpen && (
                    <p data-testid="essay-confirm-saved" className="mt-2 text-[11px] text-emerald-700">Saved how you see it: <span className="font-medium">{confirm.note}</span></p>
                  )}
                </div>
              )}

              {/* WHOLE → PARAGRAPH descent: work ONE consequential paragraph at a time (function context above) */}
              {inParagraph && (
                <div data-testid="essay-paragraph" className="mt-3 rounded-lg border border-[#8C3A2A]/25 bg-white px-3.5 py-3">
                  <p className="font-mono text-[8.5px] uppercase tracking-wider text-[#8C3A2A]/80">
                    Working one paragraph at a time · {(descent.done_count || 0) + 1} of {(descent.done_count || 0) + (descent.open_count || 0)}
                  </p>
                  {descent.active_paragraph_text && (
                    <p data-testid="essay-active-paragraph" className="text-[12.5px] text-stone-700 mt-1 mb-2 border-l-2 border-[#8C3A2A]/30 pl-2 whitespace-pre-wrap">{descent.active_paragraph_text}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button data-testid="essay-para-check" onClick={() => runTurn({ mode: "para_check" })} disabled={busy}
                      className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-50">
                      {pendingAction === "para_check" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>) : (<><Check className="h-3.5 w-3.5" /> Check this paragraph</>)}
                    </button>
                    <button data-testid="essay-para-works" onClick={() => runTurn({ mode: "para_works" })} disabled={busy}
                      className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-[#8C3A2A] disabled:opacity-50">
                      {pendingAction === "para_works" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>) : "I think this paragraph already works"}
                    </button>
                  </div>
                </div>
              )}

              {/* Route-dependent primary next action — one at a time */}
              {!goActive && !analyzing && !analysisFailed && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {(routing === "local_composition" || routing === "sentence_craft" || routing === "complete") ? (
                  <button data-testid="essay-continue-local" onClick={goLocal} disabled={busy}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[13px] font-semibold px-4 py-2.5 rounded-md hover:bg-emerald-700 disabled:opacity-40">
                    {routing === "sentence_craft" || routing === "complete" ? "Refine my sentences" : "Develop this in Composition"} <ArrowRight className="h-4 w-4" />
                  </button>
                ) : routing === "reopen_thinking" && pathway === "full" ? (
                  <>
                    <button data-testid="essay-return-to-thinking" onClick={() => returnToThinking({ reason: (tws.message || "").slice(0, 200), source_location: (go.phase || "") })} disabled={busy}
                      className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2.5 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
                      {pendingAction === "return-to-thinking" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening…</>) : (<><Brain className="h-4 w-4" /> Work this out in my thinking</>)}
                    </button>
                    <button data-testid="essay-open-thinking" onClick={() => setThinkingOpen(true)} disabled={busy}
                      className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-[#8C3A2A] disabled:opacity-50">
                      Stay here &amp; jot a note
                    </button>
                  </>
                ) : atEndpoint ? null : inEpisode ? null : isStructural ? (
                  dirty ? (
                    <>
                      <button data-testid="essay-recheck" onClick={() => runTurn({ mode: "recheck" })} disabled={busy}
                        className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-semibold px-4 py-2.5 rounded-md hover:bg-[#6B2C20] disabled:opacity-60 disabled:cursor-not-allowed">
                        {pendingAction === "recheck" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>) : (<><Check className="h-4 w-4" /> Check my revision</>)}
                      </button>
                      <button data-testid="essay-help-work" onClick={helpWork} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-[#8C3A2A] disabled:opacity-50">
                        {pendingAction === "help_work" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Setting up…</>) : (<><Sparkles className="h-3.5 w-3.5" /> Help me work on this</>)}
                      </button>
                    </>
                  ) : (
                    <>
                      <button data-testid="essay-help-work" onClick={helpWork} disabled={busy}
                        className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-semibold px-4 py-2.5 rounded-md hover:bg-[#6B2C20] disabled:opacity-60 disabled:cursor-not-allowed">
                        {pendingAction === "help_work" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Setting up…</>) : (<><Sparkles className="h-4 w-4" /> Help me work on this</>)}
                      </button>
                      <button data-testid="essay-recheck" onClick={() => runTurn({ mode: "recheck" })} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-[#8C3A2A] disabled:opacity-50">
                        {pendingAction === "recheck" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>) : (<><Check className="h-3.5 w-3.5" /> Check my revision</>)}
                      </button>
                    </>
                  )
                ) : (
                  <button data-testid="essay-recheck" onClick={() => runTurn({ mode: "recheck" })} disabled={busy}
                    className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2.5 rounded-md hover:bg-[#6B2C20] disabled:opacity-60 disabled:cursor-not-allowed">
                    {pendingAction === "recheck" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking your writing…</>) : (<><Check className="h-3.5 w-3.5" /> I've revised — take another look</>)}
                  </button>
                )}
              </div>
              )}

              {/* PASS A → PASS B BRIDGE — the persistent map above already shows the UPDATED organization.
                  Here: WHAT I WORKED OUT + that we'll bring it in ONE PART AT A TIME. Then "Work on my essay". */}
              {atEndpoint && (
                <div data-testid="essay-endpoint" className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50/70 px-3.5 py-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-emerald-800 mb-1">You worked out how your parts connect</p>
                  {attainedText && <p data-testid="essay-attained" className="text-[14px] font-semibold text-stone-900 leading-snug">“{attainedText}”</p>}
                  <p className="mt-1.5 text-[12.5px] text-stone-600">Next we'll bring that into your writing — <span className="font-medium text-stone-800">one part at a time</span>. I'll show you where on the map as we go.</p>
                  <button data-testid="essay-put-into-writing" onClick={() => runTurn({ mode: "realize_start" })} disabled={busy}
                    className="mt-2.5 inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[13px] font-semibold px-4 py-2.5 rounded-md hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed">
                    {pendingAction === "realize_start" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Setting up…</>) : (<><ArrowRight className="h-4 w-4" /> Work on my essay</>)}
                  </button>
                  {(episode.student_work || []).length > 0 && (
                    <div className="mt-2">
                      <button data-testid="essay-workedout-toggle" onClick={() => setWorkedOutOpen((o) => !o)} className="text-[11px] font-medium text-emerald-700">
                        {workedOutOpen ? "Hide what I wrote" : "What I wrote ▾"}
                      </button>
                      {workedOutOpen && (
                        <ul className="mt-1.5 space-y-1">
                          {episode.student_work.map((w, i) => (
                            <li key={i} data-testid="essay-student-work" className="text-[12px] text-stone-600 border-l-2 border-emerald-300 pl-2">{w.response}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* PASS B — DISTRIBUTED REALIZATION: one FUNCTION at a time; the map above highlights WHERE this
                  part sits in the whole. The student's own prose stays visible for reuse. */}
              {inRealize && (
                <div data-testid="essay-realize" className="mt-3 rounded-lg border border-[#8C3A2A]/25 bg-white px-3.5 py-3">
                  {/* WHAT YOU WROTE — the student's verbatim Pass-A prose, available THROUGHOUT Pass B */}
                  {studentWork.length > 0 && (
                    <div className="mb-2 rounded-md border border-stone-200 bg-stone-50 px-2.5 py-1.5">
                      <button data-testid="essay-passb-workedout-toggle" onClick={() => setPassBWorkOpen((o) => !o)} className="text-[11px] font-semibold text-[#8C3A2A]">
                        {passBWorkOpen ? "Hide what I wrote ▾" : "What I wrote ▸"}
                      </button>
                      {passBWorkOpen && (
                        <ul className="mt-1.5 space-y-1">
                          {studentWork.map((w, i) => (
                            <li key={i} data-testid="essay-passb-student-work" className="text-[12px] text-stone-700 border-l-2 border-emerald-300 pl-2">{w.response}</li>
                          ))}
                        </ul>
                      )}
                      {proseDisp && (
                        <p data-testid="essay-thinking-words-resource" className="mt-2 text-[11.5px] leading-snug text-stone-600">
                          These are your own thinking words. Use them as a resource while you revise your writing in My Writing \u2014 they aren't added to your essay for you.
                        </p>
                      )}
                    </div>
                  )}
                  {curFn ? (
                    <>
                      <p className="font-mono text-[8.5px] uppercase tracking-wider text-[#8C3A2A]/80">
                        What this part needs to do for your reader · {(episode.current_function || 0) + 1} of {realizeFns.length}
                      </p>
                      {(curFn.locations || []).some((l) => l.excerpt) && (
                        <p data-testid="essay-current-location" className="text-[12.5px] text-stone-700 mt-0.5 mb-2">
                          {(curFn.locations || []).filter((l) => l.excerpt).map((l) => `“${l.excerpt}…”`).join("  →  ")}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button data-testid="essay-check-part" onClick={() => runTurn({ mode: "realize_check" })} disabled={busy}
                          className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-50">
                          {pendingAction === "realize_check" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>) : (<><Check className="h-3.5 w-3.5" /> Check this part</>)}
                        </button>
                        <button data-testid="essay-part-works" onClick={() => runTurn({ mode: "realize_not_needed" })} disabled={busy}
                          className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-[#8C3A2A] disabled:opacity-50">
                          {pendingAction === "realize_not_needed" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>) : "I think this part already works"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-[12.5px] text-stone-600">Let's look at the whole piece now.</p>
                  )}
                </div>
              )}

              {turnError && (
                <p data-testid="essay-turn-error" className="mt-2 text-[12px] text-red-600">{turnError}</p>
              )}

              {/* STRUCTURE VIEW + developmental probe — externalizes the student's OWN parts so they can
                  coordinate them; TWS supplies structural FORMS only, never the substantive relation. */}
              {inProbe && (
                <div data-testid="essay-structure-view" className="mt-3 rounded-lg border border-[#8C3A2A]/25 bg-white px-3.5 py-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Layers className="h-3.5 w-3.5 text-[#8C3A2A]" />
                    <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#8C3A2A]/80">Let's look at the parts</p>
                  </div>
                  {episode.overarching && (
                    <div data-testid="essay-big-idea" className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5">
                      <p className="font-mono text-[8.5px] uppercase tracking-wider text-amber-800">The big idea</p>
                      <p className="text-[12.5px] text-stone-800">“{episode.overarching.excerpt}…”</p>
                    </div>
                  )}
                  <p className="text-[10.5px] text-stone-500 mb-1">{episode.overarching ? "The parts you develop" : "The parts of your draft"}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {(episode.relevant_units || []).map((u) => {
                      const sel = selUnits.includes(u.index);
                      return (
                        <button key={u.index} data-testid={`essay-unit-${u.index}`} onClick={() => toggleUnit(u.index)} disabled={busy}
                          className={`text-left text-[12.5px] leading-snug px-2.5 py-1.5 rounded-md border transition-colors ${
                            sel ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.06] text-stone-900" : "border-stone-200 bg-stone-50 text-stone-600 hover:border-[#8C3A2A]/40"}`}>
                          <span className="opacity-60">“</span>{u.excerpt}…<span className="opacity-60">”</span>
                        </button>
                      );
                    })}
                  </div>

                  {episode.phase === "forms_offered" && (episode.forms || []).length > 0 && (
                    <div data-testid="essay-forms" className="mt-2.5 flex flex-wrap gap-1.5">
                      {episode.forms.map((f) => (
                        <button key={f} data-testid={`essay-form-${f}`} onClick={() => setSelForm(f)} disabled={busy}
                          className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors ${
                            selForm === f ? "border-[#8C3A2A] bg-[#8C3A2A] text-white" : "border-stone-300 text-stone-600 hover:border-[#8C3A2A]"}`}>
                          {f}
                        </button>
                      ))}
                    </div>
                  )}

                  {(episode.constructed_relations || []).length > 0 && (
                    <div className="mt-2.5 space-y-1">
                      {episode.constructed_relations.map((r, i) => (
                        <p key={i} data-testid="essay-constructed-relation" className="text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5">
                          You worked out: {r.relation}
                        </p>
                      ))}
                    </div>
                  )}

                  <textarea data-testid="essay-probe-input" {...otNoPaste} value={probeText} onChange={(e) => setProbeText(e.target.value)}
                    placeholder="Say what you think, in your own words…"
                    className="mt-2.5 w-full min-h-[64px] text-[13px] p-2.5 rounded-md border border-stone-200 outline-none focus:border-[#8C3A2A] resize-y" />
                  <button data-testid="essay-probe-submit" onClick={sendProbe} disabled={busy || !probeText.trim()}
                    className="mt-2 inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40 disabled:cursor-not-allowed">
                    {pendingAction === "probe" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking with you…</>) : (<><ArrowRight className="h-3.5 w-3.5" /> Share my thinking</>)}
                  </button>
                </div>
              )}

              {/* Student can explain / disagree — reconstruction is fallible, so disagreement re-reads first */}
              {!goActive && !analyzing && !analysisFailed && (
              <div className="mt-3">
                <input data-testid="essay-intention" ref={explainRef} {...otNoPaste} value={intention}
                  onChange={(e) => setIntention(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && intention.trim() && !busy) runTurn({ explanation: intention.trim(), mode: "explain" }); }}
                  placeholder="Explain what you're doing, or tell me if I've misread it…"
                  className="w-full text-[13px] px-3 py-2 rounded-md border border-stone-200 outline-none focus:border-[#8C3A2A]" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button data-testid="essay-explain" onClick={() => { if (intention.trim()) runTurn({ explanation: intention.trim(), mode: "explain" }); else explainRef.current?.focus(); }} disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[12px] text-stone-500 hover:text-[#8C3A2A] disabled:opacity-40">
                    <MessageSquareText className="h-3.5 w-3.5" /> Let me explain
                  </button>
                  <button data-testid="essay-disagree" onClick={() => runTurn({ explanation: intention.trim() || "That's not what I'm doing here.", mode: "disagree" })} disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[12px] text-stone-500 hover:text-[#8C3A2A] disabled:opacity-40">
                    That's not what I mean
                  </button>
                </div>
              </div>
              )}
            </section>

            {/* MY THINKING — available for full-pathway students; the real OT representation, not a summary */}
            {pathway === "full" && (
              <section data-testid="essay-thinking" className={`rounded-2xl border p-5 transition-colors ${thinkingOpen ? "border-[#8C3A2A]/40 bg-[#8C3A2A]/[0.03]" : "border-stone-200 bg-white"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Brain className="h-4 w-4 text-[#8C3A2A]" />
                  <h2 className="font-serif text-lg text-stone-900">My Thinking</h2>
                  <button data-testid="essay-toggle-thinking" onClick={() => setThinkingOpen((o) => !o)}
                    className="ml-auto text-[11px] text-stone-400 hover:text-[#8C3A2A]">
                    {thinkingOpen ? <X className="h-3.5 w-3.5" /> : "View My Thinking"}
                  </button>
                </div>
                {thinkingOpen && (
                  <>
                    <p className="text-[11px] text-stone-500 mb-3">The thinking you developed earlier — a resource, not a blueprint. If writing this made you realize something new, add it here.</p>
                    <div className="space-y-3 max-h-[38vh] overflow-y-auto pr-1">
                      {questions.map((q) => (
                        <div key={q.id} data-testid={`essay-q-${q.id}`} className="space-y-1.5">
                          <p className="text-[12.5px] font-semibold text-[#8C3A2A] leading-snug flex items-start gap-1.5">
                            <GitBranch className="h-3.5 w-3.5 mt-0.5 shrink-0" />{q.text}
                          </p>
                          <div className="space-y-1 ml-4">
                            {directIdeas(q.id).map((it) => (
                              <p key={it.id} className="text-[13px] text-stone-700 border border-stone-200 rounded-md px-3 py-1.5 bg-white">{it.text}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                      {looseIdeas.map((it) => (
                        <p key={it.id} data-testid={`essay-idea-${it.id}`} className="text-[13px] text-stone-700 border border-stone-200 rounded-md px-3 py-1.5 bg-white">{it.text}</p>
                      ))}
                      {questions.length === 0 && looseIdeas.length === 0 && (
                        <p className="text-[12px] text-stone-400 italic">Your organized thinking will appear here.</p>
                      )}
                    </div>
                    {/* Composition can generate NEW thinking — persist it into the developmental record */}
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-stone-400 mb-1.5">Develop an idea</p>
                      <textarea data-testid="essay-new-idea" {...otNoPaste} value={newIdea} onChange={(e) => setNewIdea(e.target.value)}
                        placeholder="Work out the idea your reader needs here, in your own words…"
                        className="w-full min-h-[64px] text-[13px] p-2.5 rounded-md border border-stone-200 outline-none focus:border-[#8C3A2A] resize-y" />
                      <div className="mt-2 flex items-center gap-2">
                        <button data-testid="essay-save-idea" onClick={saveThinking} disabled={!newIdea.trim()}
                          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md border border-[#8C3A2A]/40 text-[#8C3A2A] hover:bg-[#8C3A2A]/5 disabled:opacity-40">
                          <Check className="h-3.5 w-3.5" /> Save to My Thinking
                        </button>
                        <button data-testid="essay-return-writing" onClick={() => { setThinkingOpen(false); runTurn({ mode: "recheck" }); }}
                          className="inline-flex items-center gap-1.5 text-[12px] text-stone-500 hover:text-[#8C3A2A]">
                          Back to my writing <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                        {savedNote && <span data-testid="essay-saved-note" className="text-[11px] text-emerald-700">Saved</span>}
                      </div>
                    </div>
                  </>
                )}
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
